import React, { useState, useRef, useEffect } from "react";
import { observer } from "mobx-react";
////////懸浮窗
import styles from "../styles/emissionActivityCategoryTab.module.css";
import emissionViewStore from "../../Store/marco/emissionViewStore";
//翻譯
import { useTranslation } from "react-i18next";
import commonStore from "../../Store/marco/commonStore";
import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getConstByStandard } from "../utils/getConstFileByStandard";
import {
  EMISSION_REMOVAL_MITIGATION_ENABLED,
  EMISSION_REMOVAL_MITIGATION_LABEL,
  isEmissionRemovalMitigation,
} from "../utils/emissionRemovalMitigation";

export const EmissionActivityCategoryTab = observer(() => {
  const store = emissionViewStore;
  const cmStore = commonStore;
  const CONST = getConstByStandard(store.standardId);
  const isRMWorkflow =
    EMISSION_REMOVAL_MITIGATION_ENABLED &&
    isEmissionRemovalMitigation(store.selectedScope);
  ////////根據選定的Scope去取得要渲染的Tab
  const categories = isRMWorkflow
    ? {
        label: EMISSION_REMOVAL_MITIGATION_LABEL,
        category: [
          {
            key: EMISSION_REMOVAL_MITIGATION_LABEL,
            label: EMISSION_REMOVAL_MITIGATION_LABEL,
            isDisabled: false,
          },
        ],
      }
    : cmStore?.EACategory?.find(
        (objects) => objects.label === store.selectedScope,
      );

  ////////計算container寬度以判斷要橫移多少
  const containerRef = useRef(null);
  const tabWrapRef = useRef(null);
  const TAB_WIDTH = 330;

  ////////左右offset button
  const [showLeftOffsetButton, setShowLeftOffsetButton] = useState(false);
  const [showRightOffsetButton, setShowRightOffsetButton] = useState(false);
  ////////判斷要不要show offset button
  const [ifShowOffsetButton, setIfShowOffsetButton] = useState(false); //翻譯

  // Track current translation
  const [currentTranslate, setCurrentTranslate] = useState(0);

  const { t } = useTranslation("ActivityMain");

  const updateButtonVisibility = (translateX) => {
    if (!containerRef.current || !categories?.category) return;

    const containerWidth = containerRef.current.offsetWidth;
    const totalContentWidth = categories.category.length * TAB_WIDTH;
    const maxTranslate = containerWidth - totalContentWidth;

    // Show navigation buttons if content overflows
    const overflows = totalContentWidth > containerWidth;
    setIfShowOffsetButton(overflows);

    if (overflows) {
      // logic: translateX goes from 0 to negative values
      // at 0: left hidden, right visible
      // at maxTranslate: left visible, right hidden
      // safely handle tolerance
      setShowLeftOffsetButton(translateX < -1);
      setShowRightOffsetButton(translateX > maxTranslate + 1);
    } else {
      setShowLeftOffsetButton(false);
      setShowRightOffsetButton(false);
    }
  };

  ////////右邊按下按鈕後Tab向左移 -> Move to End
  const handleOffsetRight = function () {
    if (!containerRef.current || !categories?.category) return;
    const totalContentWidth = categories.category.length * TAB_WIDTH;
    const containerWidth = containerRef.current.offsetWidth;
    const maxTranslate = containerWidth - totalContentWidth;

    // Animate to end
    applyTranslate(maxTranslate);
  };

  ////////左邊按下按鈕後Tab向右移 -> Move to Start
  const handleOffsetLeft = function () {
    // Animate to start
    applyTranslate(0);
  };

  ////////位移清零
  const handleOffsetClean = function () {
    applyTranslate(0);
  };

  const applyTranslate = (startX) => {
    if (tabWrapRef.current) {
      tabWrapRef.current.style.transform = `translateX(${startX}px)`;
      setCurrentTranslate(startX);
      updateButtonVisibility(startX);
    }
  };

  // Auto-scroll to selected tab
  useEffect(() => {
    if (!categories?.category || !containerRef.current || !store.selectedTab)
      return;

    const index = categories.category.findIndex(
      (c) => c.label === store.selectedTab,
    );
    if (index === -1) return;

    const containerWidth = containerRef.current.offsetWidth;
    const totalContentWidth = categories.category.length * TAB_WIDTH;

    // Only auto-scroll if content overflows
    if (totalContentWidth <= containerWidth) {
      handleOffsetClean();
      return;
    }

    // Calculate center position
    // Center of container = containerWidth / 2
    // Center of tab = (index * TAB_WIDTH) + (TAB_WIDTH / 2)
    // Desired TranslateX: moves the tab center to container center
    // X + TabCenter = ContainerCenter => X = ContainerCenter - TabCenter
    let targetX = containerWidth / 2 - (index * TAB_WIDTH + TAB_WIDTH / 2);

    // Clamp values
    // Max value is 0 (start)
    // Min value is containerWidth - totalContentWidth (end)
    const minTranslate = containerWidth - totalContentWidth;
    const maxTranslate = 0;

    if (targetX > maxTranslate) targetX = maxTranslate;
    if (targetX < minTranslate) targetX = minTranslate;

    applyTranslate(targetX);
  }, [store.selectedTab, categories, containerRef.current?.offsetWidth]);

  ////////當scope、view跟視窗大小改變重置滑軌位置
  useEffect(() => {
    handleOffsetClean();
    window.addEventListener("resize", handleOffsetClean); // 監聽視窗變化
    return () => window.removeEventListener("resize", handleOffsetClean); // 清理監聽
  }, [store.selectedScope, store.selectedView]);

  // Also list to resize to update visibility for static state
  useEffect(() => {
    const handleResize = () => updateButtonVisibility(currentTranslate);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentTranslate]);

  // Initial check
  useEffect(() => {
    updateButtonVisibility(currentTranslate);
  }, [categories]);

  const getTooltipByLabel = (tableTitles, label) => {
    return tableTitles.find((item) => item.label === label)?.tooltip || "";
  };

  //////// 滾輪左右滑動（直接掛原生事件確保即時響應）
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (!categories?.category) return;
      const containerWidth = el.offsetWidth;
      const totalContentWidth = categories.category.length * TAB_WIDTH;

      // 內容可完整展開 → 不攔截
      if (totalContentWidth <= containerWidth) return;

      e.preventDefault(); // 阻止頁面縱向滾動

      const minTranslate = containerWidth - totalContentWidth;
      const maxTranslate = 0;

      let newTranslate = currentTranslate - e.deltaY;
      if (newTranslate > maxTranslate) newTranslate = maxTranslate;
      if (newTranslate < minTranslate) newTranslate = minTranslate;

      applyTranslate(newTranslate);
    };

    // passive: false 才能 preventDefault
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [categories, currentTranslate]);

  return (
    <div className={styles.container} ref={containerRef}>
      {ifShowOffsetButton ? (
        <>
          <button
            className={styles.buttonLeft}
            style={{ display: showLeftOffsetButton ? "flex" : "none" }}
            onClick={handleOffsetLeft}
          >
            <ChevronLeftIcon />
          </button>
          <button
            className={styles.buttonRight}
            style={{ display: showRightOffsetButton ? "flex" : "none" }}
            onClick={handleOffsetRight}
          >
            <ChevronRightIcon />
          </button>
        </>
      ) : null}
      <div className={styles.tabWrap} ref={tabWrapRef}>
        {categories?.category?.map((item, i) => (
          <div
            key={i}
            className={`${styles.tab} ${
              store.selectedTab === item.label ? styles.blue : ""
            }`}
            onClick={() => {
              store._handleSetSelectedTab(item.label);
            }}
          >
            <p style={{ fontSize: "18px" }}>{t(item.label)}</p>

            {/**tooltip */}
            <Tooltip
              className={styles.tooltip}
              title={t(getTooltipByLabel(CONST.tableTitles, item.label))}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: "14px",
                  },
                },
              }}
            >
              <InfoIcon className={styles.infoIcon} />
            </Tooltip>

            {store.selectedView === "Emission Activity" ? (
              <>
                {cmStore.industryCode == 8550 ||
                store.selectedScope ===
                  "CO₂ Emission From Biomass/Bioenergy" ? null : item.isDisabled ||
                  cmStore.access === false ? (
                  <div className={styles.disabledIcon}>+</div>
                ) : (
                  <div
                    className={styles.addIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      store._handleShowEmissionActivityModal(item.label);
                      store._handleSetSelectedTab(item.label);
                    }}
                  >
                    +
                  </div>
                )}
              </>
            ) : store.selectedView === "Emission Factor" ? (
              <></>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
});
